import {
    Component,
    forwardRef,
    input,
    InputSignal,
    ViewChild,
    ElementRef,
    effect,
    CUSTOM_ELEMENTS_SCHEMA,
    signal,
    computed,
    Signal,
    WritableSignal,
    inject,
    DestroyRef,
    OnDestroy,
    AfterViewInit,
    HostListener,
    output,
    OutputEmitterRef,
    OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { IUser, Roles, BusinessServices, Country } from '@shs-digi-care-monorepo/interfaces';
import {
    Subject,
    debounceTime,
    distinctUntilChanged,
    switchMap,
    of,
    catchError,
    tap,
    finalize,
    filter,
    takeUntil
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { ShDropdownElement } from './sh-user-picker.model';

/**
 * User picker component with search functionality.
 * Exposes user GID as value and provides an editable dropdown with user search.
 *
 * UX model (autocomplete / combobox):
 * - Idle: the field shows the currently selected user's name; the clear (×)
 *   button removes the selection.
 * - On open (focus): the field is cleared to a fresh, empty search and the
 *   candidate list is (re)loaded, with the current selection highlighted. The
 *   previously selected value is never typed back over an in-progress search.
 * - While typing: results update live (debounced, server-side). The typed text
 *   is always under the user's control and is never overwritten.
 * - On pick: the selection is committed and the field shows the new name.
 * - On blur (focus leaves the component) without picking: the field reverts to
 *   the previously committed selection.
 *
 * Display text is bound declaratively via `displayValue` rather than poked onto
 * the element imperatively. The web component may not be fully upgraded when an
 * initial value arrives, so a one-off imperative write can be lost (the value
 * only appears after the user focuses the field). A binding is re-applied on
 * every change-detection pass, so the value shows reliably, while Lit's
 * property diffing skips redundant writes so the binding never fights typing.
 *
 * Why we don't treat "overlay closed" as "done editing":
 * SHUI's editable `sh-dropdown` auto-closes its overlay whenever its built-in
 * client-side substring filter matches none of the currently slotted items.
 * Because our results are fetched server-side (a debounce behind the typed
 * text), that filter constantly mismatches and closes the overlay mid-search.
 * Reverting on close therefore wiped the user's search and snapped the field
 * back to the selected value. Instead we finalize on `focusout`, which the
 * auto-close does not trigger, and simply re-open the overlay when our own
 * results arrive.
 */
@Component({
    selector: 'sh-user-picker',
    templateUrl: './sh-user-picker.component.html',
    standalone: true,
    imports: [CommonModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShUserPickerComponent),
            multi: true
        }
    ],
    styles: [
        `
            /*
             * Highlight the currently selected user in the list. The visible
             * background lives on .menu-item-wrapper inside the web component's
             * shadow DOM, which we cannot reach from here, so we tint the
             * sh-menu-item host with the same theme tokens SHUI uses for hover.
             * We deliberately avoid the native [active] attribute, which would
             * make the dropdown re-assert the selected label over the search.
             */
            :host ::ng-deep sh-menu-item.selected {
                display: block;
                border-radius: var(--sh-radius-control, 0.25rem);
                background: rgba(var(--ui-1, 20, 47, 74), var(--opacity-50, 0.08));
            }
        `
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShUserPickerComponent implements ControlValueAccessor, AfterViewInit, OnDestroy, OnInit {
    @ViewChild('dropdown', { static: false }) public dropdownElement: ElementRef<HTMLElement> | undefined;

    private readonly userService = inject(UserService);
    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly destroyRef = inject(DestroyRef);

    // Inputs
    public label: InputSignal<string> = input<string>('User');
    public roles: InputSignal<Roles[] | null> = input<Roles[] | null>(null);
    public teamId: InputSignal<string | null> = input<string | null>(null);
    /** Stream of the target context. Narrows the selectable users down to their roles in that stream. */
    public stream: InputSignal<BusinessServices | null> = input<BusinessServices | null>(null);
    /** Country of the target context. Narrows the selectable users down to that country's roles. */
    public country: InputSignal<Country | null> = input<Country | null>(null);
    public disabled: InputSignal<boolean> = input<boolean>(false);
    public mandatory: InputSignal<boolean> = input<boolean>(false);
    public error: InputSignal<boolean> = input<boolean>(false);
    public noBorder: InputSignal<boolean> = input<boolean>(false);
    public placeholder: InputSignal<string> = input<string>('Search for user...');
    public optionTestId: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public pageSize: InputSignal<number> = input<number>(10);

    // Outputs
    public userSelected: OutputEmitterRef<IUser | null> = output<IUser | null>();

    // Internal state
    protected currentValue: string | null = null; // GID
    protected selectedUser: WritableSignal<IUser | null> = signal(null);
    protected searchResults: WritableSignal<IUser[]> = signal([]);
    protected isLoading: WritableSignal<boolean> = signal(false);
    protected isLoadingValue: WritableSignal<boolean> = signal(false);
    protected isDisabled: WritableSignal<boolean> = signal(false);
    protected searchTerm: WritableSignal<string> = signal('');
    protected isSearching: WritableSignal<boolean> = signal(false);

    /**
     * Text shown in the dropdown's input. While searching it tracks exactly
     * what the user typed; otherwise it shows the committed selection. Bound to
     * `<sh-dropdown [value]>` so it is re-applied on every change-detection
     * pass.
     */
    protected displayValue: Signal<string> = computed(() =>
        this.isSearching() ? this.searchTerm() : this.getUserDisplayName(this.selectedUser())
    );

    private searchSubject = new Subject<string>();
    // Emits when the user starts typing a search, so an in-flight unfiltered initial page load can
    // be cancelled before its (stale) result clobbers the server-side search results.
    private searchStarted = new Subject<void>();
    private isFirstTeamIdEffect = true;

    /**
     * Tracks the most recent value we propagated upwards via `onChange`. While
     * set, any incoming `writeValue` call whose value does not match is
     * treated as a stale echo and ignored. This prevents the picker from
     * "snapping back" to a previously-selected user when the parent form is
     * slow to acknowledge a local clear/select action.
     */
    private pendingValue: string | null | undefined = undefined;

    private onChange: (value: string | null) => void = () => {};

    private onTouched: () => void = () => {};

    constructor() {
        // Load selected user when value changes
        effect(() => {
            if (this.currentValue && !this.selectedUser()) {
                this.loadUserByGid(this.currentValue);
            }
        });

        // Reset selection and load initial page when teamId changes
        effect(() => {
            const _teamId = this.teamId();
            // Skip the initial run so we don't clobber a value provided by
            // the parent form control via writeValue().
            if (!this.isFirstTeamIdEffect) {
                this.handleClear();
            }
            this.isFirstTeamIdEffect = false;
            this.loadInitialPage();
        });
    }

    public ngOnInit(): void {
        // Set up search with debounce
        this.setupSearch();
    }

    public ngAfterViewInit(): void {
        const el = this.dropdownElement?.nativeElement as ShDropdownElement | undefined;
        if (!el) {
            return;
        }
    }

    private setupSearch(): void {
        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => {
                    this.isLoading.set(true);
                    const trimmed = term.trim();
                    const searchTerm = trimmed.length > 0 ? trimmed : undefined;
                    return this.userService
                        .getUsersByRole(0, this.pageSize(), {
                            searchTerm,
                            roles: this.roles() ?? undefined,
                            teamId: this.teamId() ?? undefined,
                            stream: this.stream() ?? undefined,
                            country: this.country() ?? undefined
                        })
                        .pipe(
                            catchError((error) => {
                                console.error('Error searching users:', error);
                                return of({ data: [], total: 0, page: 0, pageSize: 0 });
                            }),
                            finalize(() => this.isLoading.set(false))
                        );
                }),
                tap((result) => {
                    this.searchResults.set(result.data || []);
                    // Our result list just changed under SHUI's feet; re-open the
                    // overlay it may have auto-closed on a no-match keystroke.
                    this.keepOpenForSearch();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    // ControlValueAccessor implementation
    public writeValue(value: string | null): void {
        // Don't overwrite state while the user is actively searching.
        if (this.isSearching()) {
            return;
        }
        // If we have a pending local change, only accept the matching ack;
        // ignore stale echoes that would otherwise snap the picker back to
        // a previously-selected user.
        if (this.pendingValue !== undefined) {
            if (value === this.pendingValue) {
                this.pendingValue = undefined;
            } else {
                return;
            }
        }
        if (value === this.currentValue) {
            return;
        }
        this.currentValue = value;
        if (value) {
            this.loadUserByGid(value);
        } else {
            this.selectedUser.set(null);
        }
    }

    public registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    /**
     * Load the first page of results without a search term.
     */
    private loadInitialPage(): void {
        this.isLoading.set(true);
        this.userService
            .getUsersByRole(0, this.pageSize(), {
                roles: this.roles() ?? undefined,
                teamId: this.teamId() ?? undefined,
                stream: this.stream() ?? undefined,
                country: this.country() ?? undefined
            })
            .pipe(
                // A search supersedes the initial list: drop this load the moment the user types,
                // so a slow response can't clobber the (correct) filtered results.
                takeUntil(this.searchStarted),
                tap((result) => {
                    this.searchResults.set(result.data || []);
                }),
                catchError((error) => {
                    console.error('Error loading initial users:', error);
                    return of({ data: [], total: 0, page: 0, pageSize: 0 });
                }),
                finalize(() => this.isLoading.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    /**
     * Handle search input change.
     *
     * The picker behaves as a free-text autocomplete: while editing, the typed
     * value is fully under the user's control and is never replaced by the
     * previously selected user.
     */
    protected handleSearchInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        // Cancel any in-flight initial (unfiltered) page load so its late response can't overwrite
        // the search results with a stale list.
        this.searchStarted.next();
        this.searchTerm.set(input.value);
        this.isSearching.set(true);
        this.searchSubject.next(input.value);
        this.keepOpenForSearch();
    }

    /**
     * Called when the dropdown's `active` property changes. The dropdown fires
     * this on itself when its overlay opens or closes (menu-items also bubble
     * their own `active-changed` events through here, which we ignore via the
     * `event.target` check).
     *
     * We only act on OPEN: entering search mode with a fresh, empty query.
     * We intentionally do NOT act on close — see the class doc. Finalizing is
     * handled by `onFocusOut`.
     */
    protected handleActiveChanged(event: Event): void {
        const dropdown = this.dropdownElement?.nativeElement as ShDropdownElement | undefined;
        if (!dropdown || event.target !== dropdown) {
            return;
        }
        if (dropdown.active && !this.isSearching()) {
            this.enterSearchMode();
        }
    }

    /**
     * Finalize editing when focus leaves the whole component. Unlike overlay
     * close, this is a reliable "the user is done" signal that SHUI's
     * auto-close does not spuriously trigger.
     */
    @HostListener('focusout', ['$event'])
    protected onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget as Node | null;
        // Focus moved to something still inside this component (e.g. a result
        // menu-item being clicked) — not a real blur, keep editing.
        if (next && this.host.nativeElement.contains(next)) {
            return;
        }
        if (!this.isSearching()) {
            return;
        }
        // Leaving search mode lets `displayValue` fall back to the committed
        // selection automatically.
        this.isSearching.set(false);
        this.searchTerm.set('');
    }

    /**
     * Begin editing: clear the field to an empty search and (re)load the
     * candidate list so the user can start typing right away. The current
     * selection (if any) is highlighted in the list rather than shown in the
     * input, so it can never overwrite the search.
     */
    private enterSearchMode(): void {
        this.searchTerm.set('');
        this.isSearching.set(true);
        this.loadInitialPage();
    }

    /**
     * SHUI's editable dropdown runs its own client-side substring filter on the
     * currently slotted items (lagging a debounce behind our server-side
     * results) and auto-closes the overlay when nothing matches. We own
     * filtering, so undo the stale hiding and re-open the overlay while a
     * search is in progress.
     */
    private keepOpenForSearch(): void {
        if (!this.isSearching()) {
            return;
        }
        const dropdown = this.dropdownElement?.nativeElement as ShDropdownElement | undefined;
        if (!dropdown) {
            return;
        }
        dropdown.removeHiddenClass?.();
        if (dropdown.active === false) {
            dropdown.active = true;
        }
    }

    /**
     * Imperatively close the dropdown overlay.
     */
    private closeDropdown(): void {
        const el = this.dropdownElement?.nativeElement as ShDropdownElement | undefined;
        if (el && el.active) {
            el.active = false;
        }
    }

    /**
     * Handle user selection from dropdown.
     */
    protected handleUserSelect(user: IUser): void {
        this.currentValue = user.gid;
        this.selectedUser.set(user);
        this.isSearching.set(false);
        this.searchTerm.set('');
        this.pendingValue = user.gid;
        this.closeDropdown();
        this.onChange(user.gid);
        this.onTouched();
        this.userSelected.emit(user);
    }

    /**
     * Handle clear selection (clear button).
     */
    protected handleClear(): void {
        this.currentValue = null;
        this.selectedUser.set(null);
        this.isSearching.set(false);
        this.searchTerm.set('');
        this.pendingValue = null;
        this.onChange(null);
        this.onTouched();
        this.userSelected.emit(null);
    }

    /**
     * Load user details by GID
     */
    private loadUserByGid(gid: string): void {
        this.isLoadingValue.set(true);
        this.userService
            .getByGID(gid)
            .pipe(
                filter((user): user is IUser => !!user),
                tap((user) => {
                    this.selectedUser.set(user);
                    this.userSelected.emit(user);
                }),
                catchError((error) => {
                    console.error('Error loading user:', error);
                    this.selectedUser.set(null);
                    return of(null);
                }),
                finalize(() => this.isLoadingValue.set(false))
            )
            .subscribe();
    }

    /**
     * Get display name for a user
     */
    protected getUserDisplayName(user: IUser | null): string {
        if (!user) return '';
        return `${user.firstName} ${user.lastName}`.trim() || user.email || user.gid;
    }

    /**
     * Check if a user is currently selected
     */
    protected isUserActive(userGid: string): boolean {
        return this.currentValue === userGid;
    }

    public ngOnDestroy(): void {
        this.searchSubject.complete();
    }
}
